import { Switch } from '@headlessui/react'
import { useSettings } from '@/settings/hooks/useSettings'
import { useAppSelector } from '@/store/store-hooks'
import { selectPromptAssistEnabled } from '@/chat-common/store/chat-ux.selectors'
import { useDispatch } from 'react-redux'
import { setPromptAssistEnabled } from '@/chat-common/store/chat-ux.slice'
import { useState, useEffect, useRef } from 'react'
import { brandedAIFriendlyName } from '@/util/enterprise'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { Header } from '@/components/header/Header'
import DefaultJurisdictionSettingsDialog from '@/settings/components/DefaultJurisdictionSettingsDialog'
import { getJurisdictionLabel } from '@/jurisdiction-mappings/jurisdiction-maps'
import { getDefaultJurisdictionAnalyticsProps } from '@/analytics/utils/matter-jurisdiction-analytics'
import { Button } from '@/components/button/Button'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import ConnectedAccounts from '@/settings/components/ConnectedAccounts'
import SectionHeader from '@/settings/components/SectionHeader'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'

export default function SettingsPage() {
  const { getSettings, updateUserSettings, defaultJurisdiction, settingsLoaded } = useSettings()
  const promptAssistEnabled = useAppSelector(selectPromptAssistEnabled)
  const [updateError, setUpdateError] = useState<boolean>(false)
  const [jurisdictionDialogOpen, setJurisdictionDialogOpen] = useState(false)
  const dispatch = useDispatch()
  const { trackEvent } = useAnalytics()

  useEffect(() => {
    getSettings()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps -- Keep the existing mount-only settings fetch behavior.

  // useRef guard (not just an empty deps array) because React.StrictMode double-invokes mount
  // effects on the same instance in development; trackEvent isn't idempotent like the fetch above.
  const settingsViewedTrackedRef = useRef(false)
  useEffect(() => {
    if (settingsViewedTrackedRef.current) return
    settingsViewedTrackedRef.current = true
    trackEvent(AnalyticsEvent.DefaultJurisdictionSettingsViewed)
  }, [trackEvent])

  const handleToggle = async () => {
    const newValue = !promptAssistEnabled
    dispatch(setPromptAssistEnabled({ enabled: newValue }))

    try {
      await updateUserSettings({ prompt_assist_enabled: newValue })
      setUpdateError(false)
    } catch (err) {
      dispatch(setPromptAssistEnabled({ enabled: !newValue }))
      setUpdateError(true)
      console.error('Failed to update user settings:', err)
    }
  }

  const handleSaveDefaultJurisdiction = async (jurisdiction: string | null) => {
    await updateUserSettings({ default_jurisdiction: jurisdiction })
    setUpdateError(false)
    trackEvent(AnalyticsEvent.DefaultJurisdictionSaved, getDefaultJurisdictionAnalyticsProps(jurisdiction))
  }

  const handleConfigureDefaultJurisdiction = () => {
    trackEvent(AnalyticsEvent.DefaultJurisdictionConfigureClicked, { hasExistingDefaultJurisdiction: Boolean(defaultJurisdiction) })
    setJurisdictionDialogOpen(true)
  }

  return (
    <div className="grow">
      <Header title="Settings" borderBottom />

      <div className="flex justify-center px-4 sm:px-8 md:px-10 mt-6">
        <div className="grow max-w-[946px] flex flex-col gap-6">
          <div className="flex flex-col overflow-hidden rounded-lg border border-brand-neutral-200 bg-white">
            <SectionHeader title="Chat Preferences" />
            <div className="px-4 py-4">
              <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-x-4">
                <div className="flex w-full flex-col sm:flex-1">
                  <div className="mb-2 flex items-center justify-between">
                    <h2 className="font-semibold leading-7 text-brand-neutral-950">Prompt Assist</h2>
                    <div className="flex items-center sm:hidden">
                      <Switch
                        checked={promptAssistEnabled}
                        onChange={handleToggle}
                        className={`${
                          promptAssistEnabled ? 'bg-brand-500' : 'bg-brand-neutral-200'
                        } group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out`}
                      >
                        <span className="sr-only">Enable Prompt Assist</span>
                        <span
                          aria-hidden="true"
                          className={`${
                            promptAssistEnabled ? 'translate-x-5' : 'translate-x-0'
                          } pointer-events-none inline-block size-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                        />
                      </Switch>
                    </div>
                  </div>
                  <p className="text-sm leading-5 tracking-[-0.15px] text-brand-neutral-600">
                    Enables {brandedAIFriendlyName} to recommend an alternative feature or ask follow-up questions in order to improve the quality of its
                    response. Prompt assist only applies to the first query in a conversation.
                  </p>
                </div>
                <div className="hidden items-center gap-x-2 sm:flex">
                  <Switch
                    checked={promptAssistEnabled}
                    onChange={handleToggle}
                    className={`${
                      promptAssistEnabled ? 'bg-brand-500' : 'bg-brand-neutral-200'
                    } group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out`}
                  >
                    <span className="sr-only">Enable Prompt Assist</span>
                    <span
                      aria-hidden="true"
                      className={`${
                        promptAssistEnabled ? 'translate-x-5' : 'translate-x-0'
                      } pointer-events-none inline-block size-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                    />
                  </Switch>
                </div>
              </div>
              {updateError && (
                <p className="mt-3 text-sm text-red-500">
                  There was an error updating your settings. Please refresh and try again. If the problem persists please let us know through the support chat
                  in the main menu.
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-col overflow-hidden rounded-lg border border-brand-neutral-200 bg-white">
            <SectionHeader title="Matter Settings" />
            <div className="py-4">
              <div className="flex flex-col gap-3 px-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="font-semibold leading-7 text-brand-neutral-950">Default Jurisdiction</p>
                  {!settingsLoaded ? (
                    <div className="flex min-h-5 items-center" role="status" aria-label="Loading default jurisdiction">
                      <CircularProgressContinuousSized size={14} thickness={6} />
                    </div>
                  ) : defaultJurisdiction ? (
                    <div className="flex flex-wrap items-center gap-x-2.5 text-sm leading-5 tracking-[-0.15px]">
                      <span className="font-semibold text-brand-neutral-800">{getJurisdictionLabel(defaultJurisdiction)}</span>
                      <span className="hidden h-3.5 border-l border-brand-neutral-400 sm:inline-block" aria-hidden="true" />
                      <span className="text-brand-neutral-600">You can change this when creating each matter.</span>
                    </div>
                  ) : (
                    <p className="text-sm leading-5 tracking-[-0.15px] text-brand-neutral-600">
                      Select a jurisdiction to auto-fill new matters. You can override anytime during matter creation.
                    </p>
                  )}
                </div>
                <Button
                  variant="tonal"
                  color={defaultJurisdiction ? 'neutral' : 'primary'}
                  size="sm"
                  onClick={handleConfigureDefaultJurisdiction}
                  disabled={!settingsLoaded}
                  className="self-start sm:self-auto"
                >
                  {defaultJurisdiction ? 'Edit' : 'Configure'}
                </Button>
              </div>
            </div>
          </div>

          {kGetFeatureFlagStatus(kFeatureFlags.DRIVE_SYNC) && <ConnectedAccounts />}
        </div>
      </div>

      <DefaultJurisdictionSettingsDialog
        open={jurisdictionDialogOpen}
        onOpenChange={setJurisdictionDialogOpen}
        initialJurisdiction={defaultJurisdiction}
        onSave={handleSaveDefaultJurisdiction}
      />
    </div>
  )
}
